import api from "../../../config/api";
import {
  readGuestCart,
  addGuestItem,
  setGuestItemQuantity,
  removeGuestItem,
  clearGuestCart,
  guestItemId,
  isGuestItemId,
} from "../../../utils/guestCart";

import {
  ADD_ITEM_TO_CART_REQUEST,
  ADD_ITEM_TO_CART_SUCCESS,
  ADD_ITEM_TO_CART_FAILURE,
  GET_CART_FAILURE,
  GET_CART_REQUEST,
  GET_CART_SUCCESS,
  REMOVE_CART_ITEM_FAILURE,
  REMOVE_CART_ITEM_REQUEST,
  REMOVE_CART_ITEM_SUCCESS,
  UPDATE_CART_ITEM_FAILURE,
  UPDATE_CART_ITEM_REQUEST,
  UPDATE_CART_ITEM_SUCCESS,
} from "./ActionType";

const isSignedIn = () => Boolean(localStorage.getItem("jwt"));

// Build a cart object in EXACTLY the shape GET /api/cart returns, so the
// reducer, cart page, mini-drawer and navbar badge all work unchanged. Products
// are fetched live rather than cached, so a guest never sees a stale price.
const buildGuestCart = async () => {
  const lines = readGuestCart();
  if (!lines.length) {
    return { cartItems: [], totalItem: 0, totalPrice: 0, totalDiscountedPrice: 0, discount: 0, couponCode: null, couponDiscount: 0 };
  }

  const results = await Promise.all(
    lines.map((l) =>
      api
        .get(`/api/products/id/${l.productId}`)
        .then(({ data }) => ({ line: l, product: data }))
        .catch(() => null) // deleted/unavailable product -> drop the line
    )
  );

  const cartItems = results
    .filter((r) => r && r.product && r.product._id)
    .map(({ line, product }) => {
      const price = Number(product.price || 0) * line.quantity;
      const discountedPrice = Number(product.discountedPrice || 0) * line.quantity;
      return {
        _id: guestItemId(line.productId, line.size),
        product,
        quantity: line.quantity,
        size: line.size,
        price,
        discountedPrice,
      };
    });

  const totalPrice = cartItems.reduce((n, i) => n + i.price, 0);
  const totalDiscountedPrice = cartItems.reduce((n, i) => n + i.discountedPrice, 0);
  return {
    cartItems,
    totalItem: cartItems.reduce((n, i) => n + i.quantity, 0),
    totalPrice,
    totalDiscountedPrice,
    discount: totalPrice - totalDiscountedPrice,
    // Coupons are validated and stored server-side, so they stay out of the
    // guest cart — a guest applies one after signing in.
    couponCode: null,
    couponDiscount: 0,
  };
};

// Push the guest cart into the signed-in user's server cart, then clear it.
// Called right after a successful sign-in / registration.
export const mergeGuestCart = () => async (dispatch) => {
  const lines = readGuestCart();
  if (!lines.length || !isSignedIn()) return;
  for (const line of lines) {
    try {
      await api.put(`/api/cart/add`, {
        productId: line.productId,
        size: line.size || undefined,
        quantity: line.quantity,
      });
    } catch (e) {
      // Out of stock or removed since it was added — skip that line rather
      // than aborting the whole merge and losing the rest of the cart.
    }
  }
  clearGuestCart();
  dispatch(getCart());
};

export const addItemToCart = (reqData) => async (dispatch) => {
  
  try {
    dispatch({ type: ADD_ITEM_TO_CART_REQUEST });

    if (!isSignedIn()) {
      // No account yet: keep it locally and report success, so the mini-cart
      // opens and the badge updates exactly as it does for a signed-in shopper.
      addGuestItem({
        productId: reqData.data?.productId,
        size: reqData.data?.size,
        quantity: reqData.data?.quantity,
      });
      dispatch({ type: ADD_ITEM_TO_CART_SUCCESS, payload: {} });
      await dispatch(getCart());
      return { success: true };
    }

    const { data } = await api.put(
      `/api/cart/add`,
      reqData.data);
    
    dispatch({
      type: ADD_ITEM_TO_CART_SUCCESS,
      payload: data,
    });
    // Refresh the full cart so the mini-cart drawer and navbar count are current.
    dispatch(getCart(reqData.jwt));
    return { success: true };
  } catch (error) {
    const message =
      error.response?.data?.message ||
      error.response?.data?.error ||
      "Could not add to cart. Please try again.";
    dispatch({ type: ADD_ITEM_TO_CART_FAILURE, payload: message });
    return { success: false, error: message };
  }
};
export const getCart = (jwt) => async (dispatch) => {
  try {
    dispatch({ type: GET_CART_REQUEST });
    // Signed out: /api/cart is behind authenticate and would 401, so serve the
    // local cart in the same shape instead.
    if (!isSignedIn()) {
      dispatch({ type: GET_CART_SUCCESS, payload: await buildGuestCart() });
      return;
    }
    const { data } = await api.get(`/api/cart/`);
    
    dispatch({
      type: GET_CART_SUCCESS,
      payload: data,
    });
  } catch (error) {
    dispatch({
      type: GET_CART_FAILURE,
      payload:
        error.response && error.response.data.message
          ? error.response.data.message
          : "Something went wrong. Please try again.",
    });
  }
};

export const removeCartItem = (reqData) => async (dispatch) => {
  try {
    dispatch({ type: REMOVE_CART_ITEM_REQUEST });
    if (isGuestItemId(reqData.cartItemId)) {
      removeGuestItem(reqData.cartItemId);
      await dispatch(getCart());
      return;
    }
    await api.delete(
      `/api/cart_items/${reqData.cartItemId}`);

    dispatch({
      type: REMOVE_CART_ITEM_SUCCESS,
      payload: reqData.cartItemId,
    });
  } catch (error) {
    dispatch({
      type: REMOVE_CART_ITEM_FAILURE,
      payload:
        error.response && error.response.data.message
          ? error.response.data.message
          : "Something went wrong. Please try again.",
    });
  }
};

export const updateCartItem = (reqData) => async (dispatch) => {
  try {
    dispatch({ type: UPDATE_CART_ITEM_REQUEST });
    if (isGuestItemId(reqData.cartItemId)) {
      setGuestItemQuantity(reqData.cartItemId, reqData.data?.quantity);
      await dispatch(getCart());
      return;
    }
    const { data } = await api.put(
      `/api/cart_items/${reqData.cartItemId}`,
      reqData.data);
    
    dispatch({
      type: UPDATE_CART_ITEM_SUCCESS,
      payload: data,
    });
  } catch (error) {
    dispatch({
      type: UPDATE_CART_ITEM_FAILURE,
      payload:
        error.response && error.response.data.message
          ? error.response.data.message
          : "Something went wrong. Please try again.",
    });
  }
};
